import { useCallback, useEffect, useState } from 'react';
import { gtmEvents } from '../../../utils/gtm';
import { useMsIdAtomValue } from '../../../context/dsUserInsights';
import { useLocalStorageSync } from '../../../hooks/useLocalStorageSync';
import { buildRedirectUrl, getUpgradeCompleteGTMConfig } from '../utils';

interface UseUpgradeCompleteReturn {
  /** Whether localStorage sync is still loading */
  isLoading: boolean;
  /** Whether the user is being redirected */
  isRedirecting: boolean;
  /** Manually execute the upgrade complete flow */
  execute: () => Promise<void>;
}

/**
 * Hook for handling upgrade completion flow
 *
 * Features:
 * - Uses useLocalStorageSync to get metaSiteId properly
 * - Fires GTM event for ds_funnel_premium
 * - Redirects to Wix dashboard with metaSiteId
 * - Auto-executes when localStorage is ready
 *
 * @returns Object with loading state, redirecting state, and manual execute function
 */
export const useUpgradeComplete = (): UseUpgradeCompleteReturn => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { isLoading: isLocalStorageLoading } = useLocalStorageSync({ syncKeys: ['msId'] });
  const metaSiteId = useMsIdAtomValue();

  const execute = useCallback(async () => {
    try {
      // Fire GTM event for ds_funnel_premium
      const gtmConfig = getUpgradeCompleteGTMConfig(metaSiteId);
      gtmEvents.premiumUpgradeComplete(gtmConfig);
      // Set redirecting state
      setIsRedirecting(true);

      // Small delay to ensure GTM event is sent
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Build redirect URL and redirect
      const redirectUrl = buildRedirectUrl(metaSiteId);
      console.log('🔄 Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
    } catch (error) {
      // Fallback redirect on error
      const fallbackUrl = buildRedirectUrl();
      window.location.href = fallbackUrl;
    }
  }, [metaSiteId]);

  // Auto-execute when component mounts and localStorage is ready
  useEffect(() => {
    if (!isLocalStorageLoading) {
      execute();
    }
  }, [isLocalStorageLoading, execute]);

  return {
    isLoading: isLocalStorageLoading,
    isRedirecting,
    execute,
  };
};
